<script lang="ts">
  import { KNOBS_BY_DOMAIN } from '../../lib/powerKnobs';
  import PowerKnobRow from '../inputs/PowerKnobRow.svelte';
  import SliderRow from '../inputs/SliderRow.svelte';
  import ColorInput from '../inputs/ColorInput.svelte';
  import Section from '../inputs/Section.svelte';
  import { SPACE_SCALE, RADIUS_SCALE, BORDER_WIDTH_SCALE, type VarOption } from '../../lib/variableScales';
  import ScaleShadowNotice from '../inputs/ScaleShadowNotice.svelte';
  import { scaleShadows } from '../../lib/tokenModel';

  let { overrides, onSet, onReset }: {
    overrides: Record<string, string>;
    onSet: (name: string, value: string) => void;
    onReset: (name: string) => void;
  } = $props();

  const BORDER_STYLES = ["solid", "dashed", "dotted"];
  const RADIUS_STEPS = ["xs", "s", "m", "l", "xl", "2xl", "full"];

  const RADIUS_FINE: Array<{ step: string; name: string; default: number; max: number; step_size: number; rawDefault: string }> = [
    { step: "xs",  name: "--sf-radius-xs",  default: 2,  max: 16, step_size: 0.5, rawDefault: "calc(2px * var(--sf-radius-scale))"  },
    { step: "s",   name: "--sf-radius-s",   default: 4,  max: 24, step_size: 0.5, rawDefault: "calc(4px * var(--sf-radius-scale))"  },
    { step: "m",   name: "--sf-radius-m",   default: 8,  max: 32, step_size: 1,   rawDefault: "calc(8px * var(--sf-radius-scale))"  },
    { step: "l",   name: "--sf-radius-l",   default: 12, max: 48, step_size: 1,   rawDefault: "calc(12px * var(--sf-radius-scale))" },
    { step: "xl",  name: "--sf-radius-xl",  default: 16, max: 64, step_size: 1,   rawDefault: "calc(16px * var(--sf-radius-scale))" },
    { step: "2xl", name: "--sf-radius-2xl", default: 24, max: 80, step_size: 2,   rawDefault: "calc(24px * var(--sf-radius-scale))" },
  ];

  const COMPONENT_TOKENS: Array<{ label: string; token: string; unit: string; min: number; max: number; step: number; default: number; rawDefault: string; help: string; variableOptions: VarOption[] }> = [
    { label: "Field radius",          token: "--sf-field-radius",       unit: "rem", min: 0, max: 2,   step: 0.05,  default: 0.5,   rawDefault: "var(--sf-radius-m)",  help: "--sf-field-radius", variableOptions: RADIUS_SCALE },
    { label: "Field padding block",   token: "--sf-field-padding-block",  unit: "rem", min: 0, max: 1, step: 0.025, default: 0.375, rawDefault: "var(--sf-space-xs)", help: "--sf-field-padding-block", variableOptions: SPACE_SCALE },
    { label: "Field padding inline",  token: "--sf-field-padding-inline", unit: "rem", min: 0, max: 2, step: 0.025, default: 0.75,  rawDefault: "var(--sf-space-s)",  help: "--sf-field-padding-inline", variableOptions: SPACE_SCALE },
  ];

  const knobs = KNOBS_BY_DOMAIN["borders"] ?? [];

  let showBorderColor = $state(false);
  let showBorderWidths = $state(false);
  let showLineStyles = $state(false);
  let showDividers = $state(false);
  let showRadiusScale = $state(false);
  let showRadiusPreview = $state(false);
  let showMediaRadius = $state(false);
  let showFineTune = $state(false);
  let showFieldShape = $state(false);

  function parseNum(val: string | undefined, fallback: number, strip?: string): number {
    if (!val) return fallback;
    const v = parseFloat(strip ? val.replace(strip, "") : val);
    return isNaN(v) ? fallback : v;
  }

  let borderScale  = $derived(parseNum(overrides["--sf-border-scale"], 1));
  let dividerWidth = $derived(parseNum(overrides["--sf-divider-width"]?.replace("px",""), 1));
  let dividerGap   = $derived(parseNum(overrides["--sf-divider-gap"]?.replace("rem",""), 1));  // ~1rem ≈ var(--sf-space-m) at default scale
  let borderColor  = $derived(overrides["--sf-color-border"] ?? "");
  let borderStyle  = $derived(overrides["--sf-border-style"] ?? "solid");
  let dividerColor = $derived(overrides["--sf-divider-color"] ?? "");
  let mediaRadius  = $derived(parseNum(overrides["--sf-media-radius"]?.replace("rem",""), 0));

  // Fixed per-step overrides that shadow the radius / border-width scale knobs
  // (the same detached-scale state the Type & Spacing panels already warn about;
  // generalised here via tokenModel.scaleShadows).
  let radiusShadow = $derived(scaleShadows(overrides).find((s) => s.family.id === "radius")?.shadowedSteps ?? []);
  let borderShadow = $derived(scaleShadows(overrides).find((s) => s.family.id === "border-width")?.shadowedSteps ?? []);
  const clearSteps = (steps: string[]) => steps.forEach(onReset);

  function getStyleCurrent(tokenName: string, defaultVal: string): string {
    return overrides[tokenName] ?? defaultVal;
  }

  function getRadiusValue(r: typeof RADIUS_FINE[0]): number {
    const raw = overrides[r.name];
    if (!raw) return r.default;
    if (/^(var|calc|clamp)\(/.test(raw.trim())) return r.default;
    const parsed = parseFloat(raw);
    return isNaN(parsed) ? r.default : parsed;
  }

  function getComponentVal(t: typeof COMPONENT_TOKENS[0]): number {
    const raw = overrides[t.token];
    if (!raw) return t.default;
    if (/^(var|calc|clamp)\(/.test(raw.trim())) return t.default;
    const parsed = parseFloat(raw);
    return isNaN(parsed) ? t.default : parsed;
  }
</script>

<div class="p-4 space-y-5">

  <!-- BORDER COLOR -->
  <Section title="Border color" bind:open={showBorderColor}>
      <ColorInput
        token="--sf-color-border"
        value={borderColor}
        placeholder="auto-derived"
        isOverridden={"--sf-color-border" in overrides}
        onSet={(v) => onSet("--sf-color-border", v)}
        onReset={() => onReset("--sf-color-border")}
      />
      <p class="text-[9px] text-slate-400 dark:text-slate-600">
        Drives --sf-color-border--strong, --subtle, --translucent automatically.
      </p>
  </Section>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- BORDER SCALE -->
  <Section title="Border widths" bind:open={showBorderWidths}>
      <ScaleShadowNotice tokens={borderShadow} scaleLabel="border-width" onClear={() => clearSteps(borderShadow)} />
      <SliderRow
        label="Border scale" value={borderScale} min={0} max={4} step={0.25}
        help="Multiplier applied to all border widths. 0 hides all borders."
        overridden={"--sf-border-scale" in overrides}
        onChange={(v) => onSet("--sf-border-scale", String(v))}
        onReset={() => onReset("--sf-border-scale")}
      />
      <SliderRow
        label="Hairline" value={parseNum(overrides["--sf-border-width-hairline"]?.replace("px",""), 0.5)} min={0} max={1} step={0.25} unit="px"
        help="--sf-border-width-hairline — ultra-thin border for retina displays"
        overridden={"--sf-border-width-hairline" in overrides}
        onChange={(v) => onSet("--sf-border-width-hairline", `${v}px`)}
        onReset={() => onReset("--sf-border-width-hairline")}
      />
      <SliderRow
        label="Box border" value={parseNum(overrides["--sf-box-border-width"]?.replace("px",""), 0)} min={0} max={4} step={0.5} unit="px"
        help="--sf-box-border-width — box-shadow border drawn without affecting layout"
        overridden={"--sf-box-border-width" in overrides}
        onChange={(v) => onSet("--sf-box-border-width", `${v}px`)}
        onReset={() => onReset("--sf-box-border-width")}
      />

      <!-- Width preview -->
      <div class="bg-black/4 dark:bg-white/4 rounded-xl border border-black/8 dark:border-white/8 p-3 space-y-2">
        {#each [1, 2, 3, 4] as base (base)}
          <div class="flex items-center gap-3">
            <span class="text-[9px] font-mono text-slate-400 dark:text-slate-600 w-14">width-{base}</span>
            <div
              class="flex-1 bg-indigo-400/50 rounded"
              style={`height: max(var(--sf-border-width-${base}, ${base}px), 0.5px)`}
            ></div>
          </div>
        {/each}
      </div>
  </Section>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- LINE STYLES -->
  <Section title="Line styles" spacing="space-y-4" bind:open={showLineStyles}>
      <div class="grid grid-cols-2 gap-3">
        {#each [
          { label: "Border", token: "--sf-border-style", default: "solid" },
          { label: "Divider", token: "--sf-divider-style", default: "solid" },
        ] as row (row.token)}
          <div>
            <div class="text-[9px] text-slate-500 mb-1.5">{row.label}</div>
            <div class="flex flex-col gap-1">
              {#each BORDER_STYLES as s (s)}
                {@const current = getStyleCurrent(row.token, row.default)}
                <button
                  onclick={() => s === row.default ? onReset(row.token) : onSet(row.token, s)}
                  class={`px-2 py-1.5 rounded-lg text-[10px] border transition-all cursor-pointer text-center ${
                    current === s
                      ? "bg-indigo-500/15 border-indigo-500/40 text-indigo-800 dark:text-indigo-200"
                      : "border-black/8 dark:border-white/8 text-slate-600 dark:text-slate-400 hover:bg-black/5 dark:hover:bg-white/5 hover:text-slate-800 dark:hover:text-slate-200"
                  }`}
                >
                  <span class="block mx-auto mb-0.5" style={`width: 20px; height: 0; border-bottom: 2px ${s} currentColor; opacity: 0.7`}></span>
                  {s}
                </button>
              {/each}
            </div>
          </div>
        {/each}
      </div>

      <!-- Combined border preview (width × style × color) -->
      <div class="bg-black/4 dark:bg-white/4 rounded-xl border border-black/8 dark:border-white/8 p-4 flex items-center justify-center gap-3">
        <div
          class="px-4 py-2 rounded-lg text-[11px] text-slate-700 dark:text-slate-300 bg-black/4 dark:bg-white/4"
          style={`border: var(--sf-border-width-1, 1px) ${borderStyle} ${borderColor || "var(--sf-color-border, #64748b)"}`}
        >
          Border sample
        </div>
        <span class="text-[9px] font-mono text-slate-400 dark:text-slate-600">width-1 · {borderStyle}</span>
      </div>
  </Section>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- DIVIDERS -->
  <Section title="Dividers" bind:open={showDividers}>
      <div class="flex items-center gap-2">
        <div class="text-[10px] font-semibold text-slate-600 dark:text-slate-400 w-24 shrink-0">Color</div>
        <ColorInput
          token="--sf-divider-color"
          value={dividerColor}
          placeholder="inherits border"
          isOverridden={"--sf-divider-color" in overrides}
          onSet={(v) => onSet("--sf-divider-color", v)}
          onReset={() => onReset("--sf-divider-color")}
        />
      </div>
      <SliderRow
        label="Width" value={dividerWidth} min={0.5} max={4} step={0.5} unit="px"
        help="--sf-divider-width"
        overridden={"--sf-divider-width" in overrides}
        onChange={(v) => onSet("--sf-divider-width", `${v}px`)}
        onReset={() => onReset("--sf-divider-width")}
        rawDefault="var(--sf-border-width-1)"
        variableOptions={BORDER_WIDTH_SCALE}
        currentRaw={overrides["--sf-divider-width"]}
        onRawSet={(v) => onSet("--sf-divider-width", v)}
      />
      <SliderRow
        label="Gap" value={dividerGap} min={0} max={4} step={0.125} unit="rem"
        help="--sf-divider-gap — spacing around divider lines"
        overridden={"--sf-divider-gap" in overrides}
        onChange={(v) => onSet("--sf-divider-gap", `${v}rem`)}
        onReset={() => onReset("--sf-divider-gap")}
        rawDefault="var(--sf-space-m)"
        variableOptions={SPACE_SCALE}
        currentRaw={overrides["--sf-divider-gap"]}
        onRawSet={(v) => onSet("--sf-divider-gap", v)}
      />

      <!-- Divider preview — a real rule between two lines so colour, width,
           style and the gap around it are all visible together. -->
      <div class="bg-black/4 dark:bg-white/4 rounded-xl border border-black/8 dark:border-white/8 p-3">
        <p class="text-[10px] text-slate-600 dark:text-slate-400 m-0">Content above</p>
        <hr style={`border:0;border-top:var(--sf-divider-width,1px) var(--sf-divider-style,solid) var(--sf-divider-color, var(--sf-color-border,#64748b));margin-block:var(--sf-divider-gap,1rem)`} />
        <p class="text-[10px] text-slate-600 dark:text-slate-400 m-0">Content below</p>
      </div>
  </Section>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- Focus ring moved to the Accessibility panel (it's an a11y control and now
       classifies there, so badge/Reset match where it's edited). -->

  <!-- RADIUS -->
  <div class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Radius</div>
  <ScaleShadowNotice tokens={radiusShadow} scaleLabel="radius" onClear={() => clearSteps(radiusShadow)} />

  <!-- Fine-tune radius steps collapsible -->
  <div>
    <button
      onclick={() => { showFineTune = !showFineTune; }}
      aria-expanded={showFineTune}
      class="w-full flex items-center justify-between text-[10px] font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors cursor-pointer py-1"
    >
      <span>Fine-tune radius steps</span>
      <span class="text-[10px] text-slate-500">{showFineTune ? "▲" : "▼"}</span>
    </button>
    {#if showFineTune}
      <div class="mt-2 space-y-2 pl-2 border-l border-black/10 dark:border-white/10">
        {#each RADIUS_FINE as r (r.name)}
          <SliderRow
            label={r.step}
            value={getRadiusValue(r)}
            min={0}
            max={r.max}
            step={r.step_size}
            unit="px"
            overridden={r.name in overrides}
            onChange={(v) => onSet(r.name, `${v}px`)}
            onReset={() => onReset(r.name)}
            rawDefault={r.rawDefault}
            currentRaw={overrides[r.name]}
            onRawSet={(v) => onSet(r.name, v)}
          />
        {/each}
      </div>
    {/if}
  </div>

  <!-- Radius preview -->
  <div>
    <button
      onclick={() => { showRadiusPreview = !showRadiusPreview; }}
      aria-expanded={showRadiusPreview}
      class="w-full flex items-center justify-between cursor-pointer mb-2"
    >
      <div class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Radius preview</div>
      <span class="text-[10px] text-slate-500">{showRadiusPreview ? "▲" : "▼"}</span>
    </button>
    {#if showRadiusPreview}
      <div class="bg-black/4 dark:bg-white/4 rounded-xl border border-black/8 dark:border-white/8 p-4">
        <div class="flex gap-3 flex-wrap">
          {#each RADIUS_STEPS as step (step)}
            <div class="flex flex-col items-center gap-1">
              <div
                class="w-10 h-10 bg-indigo-500/40 border border-indigo-500/30"
                style={`border-radius: var(--sf-radius-${step})`}
              ></div>
              <span class="text-[8px] font-mono text-slate-400 dark:text-slate-600">{step}</span>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </div>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- GLOBAL MEDIA RADIUS -->
  <Section title="Global media radius" bind:open={showMediaRadius}>
      <p class="text-[9px] text-slate-400 dark:text-slate-600">
        --sf-media-radius — 0 by default (off). Rounds every &lt;img&gt;/&lt;figure&gt; globally via a
        zero-specificity :where() rule; .sf-bg-layer and component-level radii still win.
      </p>
      <SliderRow
        label="Radius" value={mediaRadius} min={0} max={2} step={0.05} unit="rem"
        help="--sf-media-radius"
        overridden={"--sf-media-radius" in overrides}
        onChange={(v) => onSet("--sf-media-radius", `${v}rem`)}
        onReset={() => onReset("--sf-media-radius")}
        rawDefault="0"
        variableOptions={RADIUS_SCALE}
        currentRaw={overrides["--sf-media-radius"]}
        onRawSet={(v) => onSet("--sf-media-radius", v)}
      />
      <div class="bg-black/4 dark:bg-white/4 rounded-xl border border-black/8 dark:border-white/8 p-4 flex items-center justify-center">
        <div
          class="w-16 h-16 bg-indigo-500/40 border border-indigo-500/30"
          style={`border-radius: var(--sf-media-radius, 0)`}
        ></div>
      </div>
  </Section>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- FIELD SHAPE -->
  <div>
    <button
      onclick={() => { showFieldShape = !showFieldShape; }}
      aria-expanded={showFieldShape}
      class="w-full flex items-center justify-between text-[10px] font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors cursor-pointer py-1"
    >
      <span class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Field shape</span>
      <span class="text-[10px] text-slate-500">{showFieldShape ? "▲" : "▼"}</span>
    </button>
    {#if showFieldShape}
      <div class="mt-2 space-y-2">
        {#each COMPONENT_TOKENS as t (t.token)}
          <SliderRow
            label={t.label} value={getComponentVal(t)} min={t.min} max={t.max} step={t.step} unit={t.unit}
            help={t.help}
            overridden={t.token in overrides}
            onChange={(v) => onSet(t.token, `${v}${t.unit}`)}
            onReset={() => onReset(t.token)}
            rawDefault={t.rawDefault}
            variableOptions={t.variableOptions}
            currentRaw={overrides[t.token]}
            onRawSet={(v) => onSet(t.token, v)}
          />
        {/each}

        <!-- Field-shape preview — a real input styled from the field tokens, so
             radius and the block/inline padding are directly visible. -->
        <div class="bg-black/4 dark:bg-white/4 rounded-xl border border-black/8 dark:border-white/8 p-3">
          <input
            type="text"
            disabled
            placeholder="Sample field"
            class="w-full text-[11px] text-slate-600 dark:text-slate-300 bg-white/70 dark:bg-white/5 outline-none placeholder:text-slate-500"
            style={`border:var(--sf-border-width-1,1px) solid var(--sf-color-border,#64748b);border-radius:var(--sf-field-radius,0.5rem);padding-block:var(--sf-field-padding-block,0.375rem);padding-inline:var(--sf-field-padding-inline,0.75rem)`}
          />
        </div>
      </div>
    {/if}
  </div>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- ADVANCED (power knob, de-emphasised, at end) -->
  <div>
    <button
      onclick={() => { showRadiusScale = !showRadiusScale; }}
      aria-expanded={showRadiusScale}
      class="w-full flex items-center justify-between text-slate-400 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400 transition-colors cursor-pointer"
    >
      <div class="text-[10px] font-semibold uppercase tracking-widest">Advanced</div>
      <span class="text-[10px]">{showRadiusScale ? "▲" : "▼"}</span>
    </button>
    {#if showRadiusScale}
      <div class="mt-3 space-y-4">
        {#each knobs as k (k.name)}
          <PowerKnobRow
            knob={k}
            {overrides}
            onChange={(name, val) => val === null ? onReset(name) : onSet(name, val)}
          />
        {/each}
      </div>
    {/if}
  </div>
</div>
